import { Pencil, Trash2, MessageSquare, AlertCircle } from 'lucide-react';
import { FallbackRequest } from '../../types/fallback';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
  Button,
  EmptyState,
  Badge,
} from '../ui';
import { formatDate, formatDateTime } from '../../lib/formatters';

interface FallbackRequestListProps {
  requests: FallbackRequest[];
  onEdit: (request: FallbackRequest) => void;
  onDelete: (request: FallbackRequest) => void;
  isLoading?: boolean;
  isFiltered?: boolean;
}

function FallbackRequestList({
  requests,
  onEdit,
  onDelete,
  isLoading,
  isFiltered,
}: FallbackRequestListProps) {
  const getStatusVariant = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'awaiting_finalization':
        return 'warning';
      case 'pending':
        return 'error';
      default:
        return 'info';
    }
  };

  const getStatusDisplay = (status: string) => {
    switch (status) {
      case 'pending':
        return 'Pendente';
      case 'awaiting_finalization':
        return 'Aguardando';
      case 'completed':
        return 'Concluído';
      default:
        return status;
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (requests.length === 0) {
    return (
      <EmptyState
        icon={<MessageSquare className="h-12 w-12" />}
        title={isFiltered ? 'Nenhuma solicitação encontrada' : 'Nenhuma solicitação disponível'}
        description={
          isFiltered
            ? 'Tente ajustar os filtros para encontrar o que procura'
            : 'Não há solicitações de fallback disponíveis'
        }
      />
    );
  }

  return (
    <TableContainer>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Pergunta</TableHead>
            <TableHead>Contato</TableHead>
            <TableHead>Assistente</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Data</TableHead>
            <TableHead className="relative">
              <span className="sr-only">Ações</span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {requests.map(request => (
            <TableRow key={request.id}>
              <TableCell className="max-w-md whitespace-normal">
                <div className="break-words">{request.question}</div>
              </TableCell>
              <TableCell className="whitespace-normal">
                <div className="break-words">
                  {request.contact_name ? (
                    <>
                      {request.contact_name}
                      {request.contact && (
                        <div className="text-xs text-gray-500">{request.contact}</div>
                      )}
                    </>
                  ) : (
                    request.contact || '-'
                  )}
                </div>
              </TableCell>
              <TableCell>{request.assistant_name}</TableCell>
              <TableCell>
                <Badge variant={getStatusVariant(request.status)}>
                  {getStatusDisplay(request.status)}
                </Badge>
              </TableCell>
              <TableCell>{formatDateTime(request.requested_at)}</TableCell>
              <TableCell className="text-right space-x-2">
                <Button variant="ghost" onClick={() => onEdit(request)} title="Editar">
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button variant="ghost" onClick={() => onDelete(request)} title="Excluir">
                  <Trash2 className="h-4 w-4 text-red-600" />
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default FallbackRequestList;
